import { useState } from "react";
import { motion } from "framer-motion";
import { Pencil, Camera, Type, Palette, HeartHandshake, Globe2, Sparkles, Check, X, Save } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import TierBadge from "../TierBadge";
import FileUpload from "../FileUpload";

/**
 * Iter-27.66 — Hero WYSIWYG pour /asso-admin.
 *
 * Concept : montre EXACTEMENT le rendu vu par les ambassadeurs sur
 * /espace-partenaires/:id, mais avec des micro-boutons crayon overlay sur
 * chaque élément éditable. L'asso voit en direct ce qu'elle modifie.
 *
 * Non-éditables (réservés à l'admin Shariti) :
 *   - Tier badge (affiché en read-only)
 *   - Logo (réservé à l'admin pour conformité branding)
 *   - Nom de l'asso
 */
export default function EditableHero({ data, onUpdated, gratitudeCount = 0, impactCount = 0 }) {
    const [editingField, setEditingField] = useState(null); // "hero" | "tagline" | "colors" | null

    const hero = data.hero_image_url || data.cover_image;
    const accent = data.primary_color || "#EE722E";

    const saveField = async (patch) => {
        try {
            const r = await api.patch("/asso/me", patch);
            onUpdated?.(r.data);
            toast.success("Modifié");
            setEditingField(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        }
    };

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative rounded-3xl overflow-hidden mb-5 group"
                data-testid="editable-hero"
            >
                {/* Background image / video / fallback gradient */}
                <div className="relative h-64 sm:h-64 lg:h-72">
                    {hero ? (
                        <img
                            src={hero}
                            alt={data.name}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    ) : (
                        <>
                            <div
                                className="absolute inset-0"
                                style={{
                                    background: `linear-gradient(135deg, ${accent} 0%, ${accent}99 45%, #181818 100%)`,
                                }}
                            />
                            <div
                                className="absolute inset-0 opacity-25"
                                style={{
                                    backgroundImage: `radial-gradient(circle at 25% 30%, rgba(255,255,255,0.18) 0, transparent 35%), radial-gradient(circle at 80% 75%, rgba(0,0,0,0.25) 0, transparent 40%)`,
                                }}
                            />
                        </>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-shariti-black via-shariti-black/85 to-transparent" />
                    <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-shariti-black/40 to-transparent" />

                    {/* Iter-30.27 — Bouton EDIT BANNIERE repositionné : sur mobile il
                        s'affiche en chip discret en bas à gauche (à côté de "Couleurs")
                        au lieu d'être au centre, ce qui cachait nom + tier badge.
                        Desktop : icon + label flottant en bas centre au hover. */}
                    <button
                        onClick={() => setEditingField("hero")}
                        data-testid="edit-hero-image"
                        aria-label={hero ? "Changer la bannière" : "Ajouter une bannière"}
                        className="absolute top-3 right-3 sm:top-1/2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:-translate-y-1/2 z-30 inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-shariti-black/65 backdrop-blur-md border border-white/20 text-white text-[10px] sm:text-xs font-extrabold uppercase tracking-wider hover:bg-shariti-orange hover:border-shariti-orange transition-all sm:opacity-0 sm:group-hover:opacity-100"
                    >
                        <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">
                            {hero ? "Changer la bannière" : "Ajouter une bannière"}
                        </span>
                        <span className="sm:hidden">Bannière</span>
                    </button>
                </div>

                {/* Eyebrow (top-left) */}
                <div className="absolute top-3 left-3 sm:top-5 sm:left-7 z-10 max-w-[55%]">
                    <span
                        className="inline-block text-[9px] sm:text-[11px] font-extrabold uppercase tracking-[0.28em] sm:tracking-[0.32em] text-white px-2 sm:px-2.5 py-1 rounded-full whitespace-nowrap"
                        style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)" }}
                    >
                        Espace partenaire
                    </span>
                </div>
                {/* Tier badge — placé sous le bouton bannière sur mobile pour éviter overlap */}
                {data.tier && (
                    <div className="absolute top-12 right-3 sm:top-5 sm:right-7 z-10">
                        <TierBadge tier={data.tier} size="sm" />
                    </div>
                )}

                {/* Bottom block : logo + name + tagline + chips */}
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-7 z-10">
                    <div className="flex items-end gap-3 sm:gap-4 flex-wrap">
                        {/* Logo éditable : crayon overlay au hover/tap */}
                        <button
                            onClick={() => setEditingField("logo")}
                            data-testid="edit-logo"
                            aria-label="Modifier le logo"
                            className="group/logo relative shrink-0 w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-white p-1.5 flex items-center justify-center shadow-2xl hover:shadow-shariti-orange/30 transition-all"
                        >
                            {data.logo_url ? (
                                <img
                                    src={data.logo_url}
                                    alt={data.name}
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <Camera className="w-5 h-5 sm:w-6 sm:h-6 text-shariti-orange/70" />
                            )}
                            <span className="absolute -bottom-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-shariti-orange flex items-center justify-center shadow-lg ring-2 ring-shariti-deep-black opacity-90 group-hover/logo:opacity-100 group-hover/logo:scale-110 transition-all">
                                <Pencil className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
                            </span>
                        </button>

                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight drop-shadow-lg line-clamp-2">
                                {data.name}
                            </h1>
                            {/* Tagline avec petit crayon inline */}
                            <button
                                onClick={() => setEditingField("tagline")}
                                data-testid="edit-tagline"
                                className="group/tag mt-1 text-left flex items-center gap-2 max-w-2xl rounded-lg hover:bg-white/5 px-2 -mx-2 py-0.5 transition-all"
                            >
                                <span className="text-xs sm:text-base text-white/90 font-light drop-shadow flex-1 line-clamp-2">
                                    {data.tagline || (
                                        <span className="italic text-white/55">
                                            Ajoute une phrase d'accroche…
                                        </span>
                                    )}
                                </span>
                                <Pencil className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/70 opacity-70 sm:opacity-50 transition-opacity shrink-0" />
                            </button>
                        </div>
                    </div>

                    {/* Promise chips */}
                    <div className="mt-3 sm:mt-4 flex items-center gap-2 flex-wrap">
                        <PromiseChip
                            icon={HeartHandshake}
                            label="Remerciements"
                            count={gratitudeCount}
                            color={accent}
                        />
                        <PromiseChip
                            icon={Globe2}
                            label="Histoires d'impact"
                            count={impactCount}
                            color={accent}
                        />
                    </div>
                </div>

                {/* Bouton couleurs — placé bottom-left/right pour ne pas overlap le contenu */}
                <button
                    onClick={() => setEditingField("colors")}
                    data-testid="edit-colors"
                    className="absolute top-3 left-3 sm:top-auto sm:bottom-5 sm:right-5 sm:left-auto z-20 hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-shariti-black/60 backdrop-blur-md border border-white/20 text-white text-[10px] font-extrabold uppercase tracking-wider hover:bg-shariti-orange hover:border-shariti-orange transition-all"
                >
                    <Palette className="w-3 h-3" />
                    Couleurs
                </button>
            </motion.div>

            {/* Iter-30.27 — Sur mobile, "Couleurs" devient un petit chip indépendant
                sous le hero plutôt que superposé (évite l'overlap avec le tier badge). */}
            <div className="-mt-2 mb-4 flex justify-end sm:hidden">
                <button
                    onClick={() => setEditingField("colors")}
                    data-testid="edit-colors-mobile"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/75 text-[10px] font-extrabold uppercase tracking-wider transition-all"
                >
                    <Palette className="w-3 h-3" />
                    Couleurs
                </button>
            </div>

            {/* Modales d'édition */}
            {editingField === "hero" && (
                <EditModal title="Image bannière de ton espace" onClose={() => setEditingField(null)}>
                    <p className="text-xs text-white/55 mb-4 leading-relaxed">
                        Choisis une photo qui inspire — équipe sur le terrain, bénéficiaires, action concrète. Format recommandé : 1600×600 px minimum.
                    </p>
                    <FileUpload
                        kind="image"
                        currentUrl={data.hero_image_url}
                        onUploaded={(url) => saveField({ hero_image_url: url })}
                        testId="hero-image-upload"
                    />
                    {data.hero_image_url && (
                        <button
                            onClick={() => saveField({ hero_image_url: "" })}
                            className="mt-3 text-xs text-red-300 hover:text-red-200 font-bold"
                        >
                            Retirer la bannière
                        </button>
                    )}
                </EditModal>
            )}

            {editingField === "logo" && (
                <EditModal title="Logo de ton association" onClose={() => setEditingField(null)}>
                    <p className="text-xs text-white/55 mb-4 leading-relaxed">
                        Format recommandé : carré (1:1), PNG transparent ou JPEG haute résolution. Le logo s'affiche sur fond blanc — privilégie une version lisible.
                    </p>
                    {data.logo_url && (
                        <div className="mb-4 flex justify-center">
                            <div className="w-24 h-24 rounded-2xl bg-white p-2 flex items-center justify-center shadow-xl">
                                <img
                                    src={data.logo_url}
                                    alt="Logo actuel"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </div>
                    )}
                    <FileUpload
                        kind="image"
                        currentUrl={data.logo_url}
                        onUploaded={(url) => saveField({ logo_url: url })}
                        testId="logo-upload"
                    />
                    {data.logo_url && (
                        <button
                            onClick={() => saveField({ logo_url: "" })}
                            className="mt-3 text-xs text-red-300 hover:text-red-200 font-bold"
                        >
                            Retirer le logo
                        </button>
                    )}
                </EditModal>
            )}

            {editingField === "tagline" && (
                <TaglineModal
                    initial={data.tagline}
                    onSave={(v) => saveField({ tagline: v })}
                    onClose={() => setEditingField(null)}
                />
            )}

            {editingField === "colors" && (
                <ColorsModal
                    primary={data.primary_color || "#EE722E"}
                    accent={data.accent_color || "#FCDB53"}
                    onSave={(payload) => saveField(payload)}
                    onClose={() => setEditingField(null)}
                />
            )}
        </>
    );
}


function PromiseChip({ icon: Icon, label, count, color }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full"
            style={{
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(8px)",
                color: "#fff",
                border: `1px solid ${color}55`,
            }}
        >
            <Icon className="w-3.5 h-3.5" style={{ color }} />
            <span>{label}</span>
            <span
                className="ml-0.5 px-1.5 rounded-full text-[10px] font-extrabold"
                style={{ background: color, color: "#181818" }}
            >
                {count}
            </span>
        </span>
    );
}


function EditModal({ title, children, onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-shariti-black border border-white/10 rounded-3xl w-full max-w-md p-6 sm:p-7"
                data-testid="edit-modal"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-extrabold text-white flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-shariti-yellow" />
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-white/5 text-white/60"
                        aria-label="Fermer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </motion.div>
        </motion.div>
    );
}


function TaglineModal({ initial, onSave, onClose }) {
    const [v, setV] = useState(initial || "");
    return (
        <EditModal title="Phrase d'accroche" onClose={onClose}>
            <p className="text-xs text-white/55 mb-3">
                Une phrase courte qui résume ta mission et touche les ambassadeurs au cœur.
            </p>
            <textarea
                value={v}
                onChange={(e) => setV(e.target.value)}
                rows={3}
                maxLength={200}
                placeholder="Ex : Pour les droits humains partout"
                data-testid="tagline-input"
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white text-sm outline-none focus:border-shariti-yellow/40 resize-none"
                autoFocus
            />
            <div className="mt-1 text-right text-[10px] text-white/40">{v.length}/200</div>
            <div className="mt-4 flex justify-end gap-2">
                <button
                    onClick={onClose}
                    className="bg-white/5 hover:bg-white/10 text-white/80 font-bold px-4 py-2 rounded-2xl text-sm"
                >
                    Annuler
                </button>
                <button
                    onClick={() => onSave(v.trim())}
                    data-testid="tagline-save"
                    className="inline-flex items-center gap-2 bg-shariti-yellow text-shariti-black font-extrabold px-4 py-2 rounded-2xl text-sm"
                >
                    <Check className="w-4 h-4" />
                    Enregistrer
                </button>
            </div>
        </EditModal>
    );
}


function ColorsModal({ primary, accent, onSave, onClose }) {
    const [p, setP] = useState(primary);
    const [a, setA] = useState(accent);
    return (
        <EditModal title="Couleurs de ton espace" onClose={onClose}>
            <p className="text-xs text-white/55 mb-4">
                La couleur primaire teinte la bannière et les accents. La couleur d'accent met en avant les boutons et les chips.
            </p>
            <div className="space-y-4">
                <ColorPickerRow
                    label="Couleur primaire"
                    value={p}
                    onChange={setP}
                    testId="primary-color-picker"
                />
                <ColorPickerRow
                    label="Couleur d'accent"
                    value={a}
                    onChange={setA}
                    testId="accent-color-picker"
                />
            </div>
            <div className="mt-5 flex justify-end gap-2">
                <button
                    onClick={onClose}
                    className="bg-white/5 hover:bg-white/10 text-white/80 font-bold px-4 py-2 rounded-2xl text-sm"
                >
                    Annuler
                </button>
                <button
                    onClick={() => onSave({ primary_color: p, accent_color: a })}
                    data-testid="colors-save"
                    className="inline-flex items-center gap-2 bg-shariti-yellow text-shariti-black font-extrabold px-4 py-2 rounded-2xl text-sm"
                >
                    <Save className="w-4 h-4" />
                    Enregistrer
                </button>
            </div>
        </EditModal>
    );
}


function ColorPickerRow({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="text-[10px] font-thin tracking-[0.2em] uppercase text-white/50 mb-1 block">
                {label}
            </label>
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-3 py-2">
                <input
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testId}
                    className="w-10 h-10 rounded-lg border-0 bg-transparent cursor-pointer"
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="#000000"
                    className="flex-1 bg-transparent text-white text-sm font-mono outline-none"
                />
            </div>
        </div>
    );
}
